import { AdobeIMS } from '@identity/imslib';
import ImsActions from './ims-actions';
import ImsData, { type ImsLibData } from './ims-data';

interface AdobeIMSInstance {
    disableLogging: () => void;
    initialize: () => void;
    getReleaseFlags: () => Promise<unknown>;
    signIn: () => void;
    signOut: () => void;
    getAccessToken: () => { token: string };
    refreshToken: () => void;
    isSignedInUser: () => boolean;
    reAuthenticate: (options?: Record<string, unknown>, force?: string) => void;
    getProfile: () => Promise<unknown>;
    validateToken: () => Promise<boolean>;
    listSocialProviders: () => Promise<unknown>;
    signInWithSocialProvider: (provider: string) => void;
    avatarUrl: (userId: string) => string;
}

export default class IMS {
    imsActions: ImsActions;

    adobeIMS: AdobeIMSInstance;

    imsEvents: unknown = null;

    private static instance: IMS | null = null;

    constructor(onStateChanged?: ((newState: ImsLibData) => void) | null) {
        const imsData = new ImsData(onStateChanged ?? null, {
            client_id: process.env.IMS_CLIENT_ID as string,
            locale: 'en_US',
            scope: 'AdobeID,openid',
            environment: process.env.IMS_ENV as string,
            key: 'adobeIMS',
            logsEnabled: true,
            redirect_uri: window.location.href,
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.adobeIMS = new AdobeIMS(imsData.adobeIdData);
        this.adobeIMS.disableLogging();
        this.imsActions = new ImsActions(this.adobeIMS, imsData);
        this.adobeIMS.initialize();
    }

    /**
     * Get the singleton IMS instance.
     * This ensures the Adobe IMS library can properly read cached tokens from localStorage.
     * Creating multiple instances can cause tokens to not be retrieved from cache.
     */
    static getInstance(onStateChanged?: ((newState: ImsLibData) => void) | null): IMS {
        if (!IMS.instance) {
            IMS.instance = new IMS(onStateChanged);
        }
        return IMS.instance;
    }

    /**
     * Reset the singleton instance (useful for testing or logout scenarios)
     */
    static resetInstance(): void {
        IMS.instance = null;
    }

    isLoggedIn = () => {
        return this.imsActions.isSignedInUser();
    };

    login = () => {
        return this.imsActions.signIn();
    };

    getAccessToken = () => {
        return this.imsActions.getAccessToken();
    };

    logout = () => {
        return this.imsActions.signOut();
    };

    getProfile = async () => {
        return await this.imsActions.getProfile();
    };

    avatarUrl = (userId: string) => {
        return this.imsActions.avatarUrl(userId);
    };
}
